// Site-wide announcement banner. Reads from site_settings; renders nothing
// when disabled. Dismissible per-session via localStorage (keyed by message
// hash so a new message reappears).
import { useEffect, useState } from "react";
import { X, Megaphone, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const VARIANT_STYLES: Record<string, string> = {
  info: "bg-primary/10 border-b border-primary/30 text-primary",
  success: "bg-emerald-500/10 border-b border-emerald-500/30 text-emerald-400",
  warning: "bg-amber-500/10 border-b border-amber-500/30 text-amber-400",
  promo: "bg-gradient-to-r from-primary/15 via-primary/10 to-primary/15 border-b border-primary/40 text-primary",
};

const VARIANT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  info: Megaphone,
  success: CheckCircle2,
  warning: AlertTriangle,
  promo: Sparkles,
};

function hashStr(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return String(h);
}

const AnnouncementBanner = () => {
  const { settings } = useSiteSettings();
  const [dismissed, setDismissed] = useState(false);
  const key = `hl:banner_dismissed:${hashStr(settings.banner_message)}`;

  useEffect(() => {
    if (typeof window === "undefined") return;
    setDismissed(window.localStorage.getItem(key) === "1");
  }, [key]);

  if (!settings.banner_enabled || !settings.banner_message.trim() || dismissed) {
    return null;
  }

  const Icon = VARIANT_ICONS[settings.banner_variant] ?? Megaphone;
  const styles = VARIANT_STYLES[settings.banner_variant] ?? VARIANT_STYLES.info;

  return (
    <div className={`${styles} px-4 py-2 text-xs font-mono`} role="status">
      <div className="max-w-6xl mx-auto flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 shrink-0" />
        <p className="flex-1 text-foreground/90 truncate sm:whitespace-normal">
          <span className="text-current font-semibold mr-1">{settings.banner_message}</span>
          {settings.banner_link_url && settings.banner_link_label && (
            <a
              href={settings.banner_link_url}
              className="underline hover:no-underline ml-1"
              target={settings.banner_link_url.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
            >
              {settings.banner_link_label}
            </a>
          )}
        </p>
        <button
          type="button"
          onClick={() => {
            window.localStorage.setItem(key, "1");
            setDismissed(true);
          }}
          aria-label="Dismiss announcement"
          className="p-1 rounded hover:bg-foreground/10 text-foreground/70 hover:text-foreground transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
